import { Checkbox, Popconfirm } from 'antd';
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/app.hooks';
import { FilterByBooleanDropDown, FilterByDateSwap, FilterWithSwapOption } from '../../../../common/components/DataTable/DataTableFilters';
import { IMainTable, ISearch } from '../../../../common/models/common';
import { useHistory } from 'react-router-dom';
import DataTable from '../../../../common/components/DataTable';
import ability, { Can } from '../../../../common/ability';
import { Action, Page } from '../../../../common/constants/pageAction';
import {
  clearConfigSoftwareMessages,
  configSoftwareSelector,
  setTableColumnSelection,
} from '../../../../store/master/configSoftware/configSoftware.reducer';
import {
  deleteConfigSoftware,
  searchConfigSoftware,
} from '../../../../store/master/configSoftware/configSoftware.action';
import { AlignType } from 'rc-table/lib/interface';
import configSoftwareService from '../../../../services/master/configSoftware/configSoftware.service';
import _ from 'lodash';
import { showDateFromApi } from '../../../../common/helperFunction';

const MainTable: React.ForwardRefRenderFunction<unknown, IMainTable> = (props, ref) => {
  const {
    setSelectedId,
    setShowSelectedListModal,
    setValuesForSelection,
    isMultiple,
    tableButtons,
  } = props;
  const configSoftware = useAppSelector(configSoftwareSelector);
  const dispatch = useAppDispatch();
  const dataTableRef = useRef(null);
  const history = useHistory();
  const [ObjectForColumnFilter, setObjectForColumnFilter] = useState({});

  useImperativeHandle(ref, () => ({
    refreshData() {
      dataTableRef?.current.refreshData();
    },
  }));

  useEffect(() => {
    if (isMultiple) {
      dataTableRef?.current.getValuesForSelection();
    }
  }, [isMultiple]);

  const exportExcelFile = (searchData: ISearch) => {
    return configSoftwareService.exportExcelFile(searchData);
  };

  const FilterByDateSwapTable = (dataIndex: string, tableName: string, form: any) => {
    return FilterByDateSwap(dataIndex, tableName, form, null, ObjectForColumnFilter);
  };

  const FilterBySwap = (dataIndex: string, form, isRelativeDropDownFlag?: boolean) => {
    return FilterWithSwapOption(
      dataIndex,
      configSoftware.search.tableName,
      form,
      null,
      ObjectForColumnFilter,
      null,
      null,
      isRelativeDropDownFlag
    );
  };

  const getTableColumns = (form) => {
    return [
      {
        title: <span className="dragHandler">ID</span>,
        column: 'id',
        sorter: true,
        ellipsis: true,
        children: [
          {
            title: FilterBySwap('id', form),
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Name</span>,
        column: 'SoftwareName',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_name', form),
            dataIndex: 'software_name',
            key: 'software_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Device Type</span>,
        column: 'SoftwareDeviceType',
        sorter: true,
        children: [
          {
            title: FilterBySwap('device_type_id', form, true),
            dataIndex: 'device_type_name',
            key: 'device_type_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Family</span>,
        column: 'SoftwareFamily',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_family_id', form, true),
            dataIndex: 'family_name',
            key: 'family_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Version</span>,
        column: 'SoftwareVersion',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_version_id', form, true),
            dataIndex: 'version_name',
            key: 'version_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Metric</span>,
        column: 'SoftwareMetric',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_metric_id', form, true),
            dataIndex: 'metric_name',
            key: 'metric_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Publisher</span>,
        column: 'SoftwarePublisher',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_publisher_id', form, true),
            dataIndex: 'publisher_name',
            key: 'publisher_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Product</span>,
        column: 'SoftwareProduct',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_product_id', form, true),
            dataIndex: 'product_name',
            key: 'product_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">Software Edition</span>,
        column: 'SoftwareEdition',
        sorter: true,
        children: [
          {
            title: FilterBySwap('software_edition_id', form, true),
            dataIndex: 'edition_name',
            key: 'edition_name',
            ellipsis: true,
          },
        ],
      },
      {
        title: <span className="dragHandler">End of Life</span>,
        column: 'EndofLifeUpdated',
        sorter: true,
        children: [
          {
            title: FilterByDateSwapTable('end_of_life', configSoftware.search.tableName, form),
            dataIndex: 'end_of_life',
            key: 'end_of_life',
            ellipsis: true,
            render: (date: Date) => (!_.isNull(date) ? showDateFromApi(date) : ''),
          },
        ],
      },
      {
        title: <span className="dragHandler">Is Operating System</span>,
        column: 'Is Operating System',
        sorter: true,
        children: [
          {
            title: FilterByBooleanDropDown(
              'is_operating_system',
              configSoftware.search.tableName,
              ObjectForColumnFilter
            ),
            dataIndex: 'is_operating_system',
            key: 'is_operating_system',
            ellipsis: true,
            render: (value: boolean) =>
              !_.isNull(value) ? (
                value ? (
                  <Checkbox checked disabled />
                ) : (
                  <Checkbox checked={false} disabled />
                )
              ) : (
                ''
              ),
            align: 'center' as AlignType,
          },
        ],
      },
    ];
  };

  const removeConfigSoftware = (id: number) => {
    dispatch(deleteConfigSoftware(id));
  };
  const tableAction = (_, data: any) => (
    <div className="btns-block">
      <Can I={Action.Update} a={Page.ConfigSoftware}>
        <a
          className="action-btn"
          onClick={() => {
            setSelectedId(data.id);
            history.push(`/administration/config-software/${data.id}`);
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/assets/images/ic-edit.svg`} alt="" />
        </a>
      </Can>
      <Can I={Action.Delete} a={Page.ConfigSoftware}>
        <Popconfirm title="Delete Record?" onConfirm={() => removeConfigSoftware(data.id)}>
          <a href="#" title="" className="action-btn">
            <img src={`${process.env.PUBLIC_URL}/assets/images/ic-delete.svg`} alt="" />
          </a>
        </Popconfirm>
      </Can>
    </div>
  );

  return (
    <>
      <DataTable
        ref={dataTableRef}
        showAddButton={ability.can(Action.Add, Page.ConfigSoftware)}
        showDelete={ability.can(Action.Delete, Page.ConfigSoftware)}
        setSelectedId={setSelectedId}
        tableAction={tableAction}
        exportExcelFile={exportExcelFile}
        getTableColumns={getTableColumns}
        reduxSelector={configSoftwareSelector}
        searchTableData={searchConfigSoftware}
        clearTableDataMessages={clearConfigSoftwareMessages}
        setTableColumnSelection={setTableColumnSelection}
        setShowSelectedListModal={setShowSelectedListModal}
        setValuesForSelection={setValuesForSelection}
        showBulkUpdate={ability.can(Action.Update, Page.ConfigSoftware)}
        setObjectForColumnFilter={setObjectForColumnFilter}
        globalSearchExist={false}
        tableButtons={tableButtons}
      />
    </>
  );
};

export default forwardRef(MainTable);
